/*
 * Obraven collection listing (sections/main-collection-product-grid.liquid, snippets/facets.liquid).
 *
 * <collection-facets>
 * - Filter and sort changes re-render the section through the Section Rendering API and keep the URL
 *   in sync (pushState; Back/Forward re-render from the URL), so filters survive a refresh.
 * - Active filter chips and Clear all links ([data-facets-link]) apply their URL without a reload.
 * - Show/Hide filters and the 3/4 column toggle (saved in localStorage).
 * - Below 990px the filter panel is a bottom sheet: focus trap, Esc, focus return, body scroll lock.
 * - Load more appends the next page's cards, updates ?page= and moves focus to the first new card.
 * - Fires Shopify's CollectionUpdateEvent like Dawn's facets.js.
 *
 * <price-range>: two-thumb range slider kept in sync with the From/To inputs.
 */
(() => {
  if (customElements.get('collection-facets')) return;

  const STORE_COLUMNS = 'obraven:collection-columns';
  const STORE_FILTERS = 'obraven:collection-filters';
  const desktop = window.matchMedia('(min-width: 990px)');
  const cache = new Map();
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const save = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      // Storage unavailable: the choice simply isn't remembered.
    }
  };

  // Section Rendering responses build links from the request URL; drop the section_id they carry.
  const stripSectionId = (root) => {
    root.querySelectorAll('a[href*="section_id="]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.delete('section_id');
      link.setAttribute('href', url.pathname + url.search);
    });
  };

  const announce = (region, message) => {
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 100);
  };

  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.panel = this.querySelector('[data-facets-panel]');
      this.sortSelect = this.querySelector('[data-sort-select]');
      this.liveRegion = this.querySelector('[data-facets-live]');
      this.requestId = 0;
      this.lastParams = window.location.search.slice(1);
      this.debouncedSubmit = debounce(() => this.submit(), 350);

      this.onClick = this.onClick.bind(this);
      this.onPanelKeydown = this.onPanelKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.addEventListener('click', this.onClick);
      if (this.form) {
        this.form.addEventListener('change', (event) => this.onFormChange(event));
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });
      }
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => this.onSortSelect());
        this.sortSelect.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.onSortSelect();
        });
      }
      this.panel?.addEventListener('keydown', this.onPanelKeydown);
      window.addEventListener('popstate', this.onPopState);
      desktop.addEventListener('change', this.onBreakpoint);

      this.syncToggles();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      desktop.removeEventListener('change', this.onBreakpoint);
      document.body.classList.remove('overflow-hidden-tablet');
    }

    onClick(event) {
      const link = event.target.closest('[data-facets-link]');
      if (link) {
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        url.searchParams.delete('section_id');
        url.searchParams.delete('page');
        this.render(url.searchParams.toString());
        return;
      }

      const loadMore = event.target.closest('[data-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }

      if (event.target.closest('[data-filters-toggle]')) {
        this.toggleFilters();
        return;
      }

      const columns = event.target.closest('[data-columns-button]');
      if (columns) {
        this.dataset.columns = columns.dataset.columnsButton;
        save(STORE_COLUMNS, this.dataset.columns);
        this.syncToggles();
        return;
      }

      const opener = event.target.closest('[data-sheet-open]');
      if (opener) {
        this.openSheet(opener);
        return;
      }

      if (event.target.closest('[data-sheet-close]')) this.closeSheet();
    }

    onFormChange(event) {
      const target = event.target;
      if (target.name === 'sort_by' && this.sortSelect) this.sortSelect.value = target.value;
      // Range thumbs have no name: they report through the From/To inputs, then submit on release.
      if (target.matches('[data-range-min], [data-range-max], [data-price-min], [data-price-max]')) {
        this.submit();
        return;
      }
      this.debouncedSubmit();
    }

    onSortSelect() {
      const value = this.sortSelect.value;
      const radio = this.form?.querySelector(`input[name="sort_by"][value="${CSS.escape(value)}"]`);
      if (radio) radio.checked = true;
      this.submit();
    }

    submit() {
      if (!this.form) return;
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form).entries()) {
        if (value === '') continue;
        if (key === 'sort_by' && value === this.dataset.defaultSort) continue;
        params.append(key, value);
      }
      this.render(params.toString());
    }

    onPopState() {
      const params = window.location.search.slice(1);
      if (params === this.lastParams) return;
      this.render(params, { updateURL: false });
    }

    async render(params, { updateURL = true } = {}) {
      const requestId = ++this.requestId;
      this.lastParams = params;
      this.classList.add('is-loading');
      if (updateURL) {
        history.pushState({ facets: true }, '', `${window.location.pathname}${params ? `?${params}` : ''}`);
      }

      const update = this.startUpdateEvent(params);
      const url = `${window.location.pathname}?section_id=${this.sectionId}${params ? `&${params}` : ''}`;
      let html = cache.get(url);
      try {
        if (!html) {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          cache.set(url, html);
        }
      } catch (error) {
        update?.reject(error);
        if (requestId === this.requestId) window.location.assign(`${window.location.pathname}?${params}`);
        return;
      }
      if (requestId !== this.requestId) return;

      const source = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
      if (!source) {
        window.location.assign(`${window.location.pathname}?${params}`);
        return;
      }
      this.apply(source);
      this.classList.remove('is-loading');

      const count = Number(source.dataset.resultsCount) || 0;
      update?.resolve(count);
    }

    apply(source) {
      stripSectionId(source);
      const active = document.activeElement;
      const wasInside = Boolean(active && active !== this && this.contains(active));
      const activeId = wasInside ? active.id : '';
      const scroller = this.querySelector('[data-facets-scroll]');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      source.querySelectorAll('[data-facets-update]').forEach((fresh) => {
        const current = this.querySelector(`[data-facets-update="${fresh.dataset.facetsUpdate}"]`);
        if (current) current.innerHTML = fresh.innerHTML;
      });
      this.dataset.resultsCount = source.dataset.resultsCount || '';
      if (scroller) scroller.scrollTop = scrollTop;

      const freshSelect = source.querySelector('[data-sort-select]');
      if (freshSelect && this.sortSelect) {
        this.sortSelect.innerHTML = freshSelect.innerHTML;
        this.sortSelect.value = freshSelect.value;
      }

      // Put focus back where it was (same id), or somewhere sensible if that control is gone.
      const lost = !document.activeElement || document.activeElement === document.body;
      if (activeId && document.getElementById(activeId)) {
        document.getElementById(activeId).focus({ preventScroll: true });
      } else if (wasInside && (lost || !this.contains(document.activeElement))) {
        const sheetOpen = this.panel?.classList.contains('is-open');
        const fallback = sheetOpen
          ? this.panel.querySelector('.ob-facets__close')
          : this.querySelector('.ob-collection__chip') ||
            this.querySelector('[data-filters-toggle]') ||
            this.sortSelect;
        fallback?.focus({ preventScroll: true });
      }

      const countText = this.querySelector('[data-facets-update="count"]')?.textContent.trim();
      if (countText) announce(this.liveRegion, countText);
    }

    async loadMore(link) {
      if (this.loadingMore) return;
      this.loadingMore = true;
      link.classList.add('is-loading');
      link.setAttribute('aria-disabled', 'true');

      const url = new URL(link.href, window.location.origin);
      const page = url.searchParams.get('page');
      url.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(url.pathname + url.search);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const source = new DOMParser().parseFromString(await response.text(), 'text/html').getElementById(this.id);
        if (!source) throw new Error('Section not found');
        stripSectionId(source);

        const grid = this.querySelector('[data-collection-grid]');
        const items = Array.from(source.querySelectorAll('[data-collection-grid] > li'));
        grid.append(...items);

        const more = this.querySelector('[data-collection-more]');
        const freshMore = source.querySelector('[data-collection-more]');
        if (more && freshMore) more.replaceWith(freshMore);

        if (page) {
          const pageUrl = new URL(window.location.href);
          pageUrl.searchParams.set('page', page);
          history.replaceState(history.state, '', pageUrl.pathname + pageUrl.search);
          this.lastParams = pageUrl.search.slice(1);
        }

        const firstLink = items[0]?.querySelector('a[href]:not([tabindex="-1"])');
        firstLink?.focus({ preventScroll: true });
        const viewed = this.querySelector('.ob-collection__viewed')?.textContent.trim();
        if (viewed) announce(this.liveRegion, viewed);
      } catch (error) {
        window.location.assign(link.href);
      } finally {
        this.loadingMore = false;
      }
    }

    toggleFilters() {
      const hide = !this.hasAttribute('data-filters-hidden');
      this.toggleAttribute('data-filters-hidden', hide);
      save(STORE_FILTERS, hide ? 'hidden' : 'open');
      this.syncToggles();
    }

    syncToggles() {
      const open = !this.hasAttribute('data-filters-hidden');
      this.querySelectorAll('[data-filters-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', String(open));
      });
      this.querySelectorAll('[data-columns-button]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.columnsButton === this.dataset.columns));
      });
    }

    openSheet(trigger) {
      if (!this.panel || desktop.matches) return;
      this.sheetTrigger = trigger;
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.classList.add('is-open');
      this.classList.add('is-sheet-open');
      document.body.classList.add('overflow-hidden-tablet');
      this.querySelectorAll('[data-sheet-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));

      const sortTarget =
        trigger.dataset.sheetOpen === 'sort'
          ? this.panel.querySelector('[data-facets-sort-group] input:checked') ||
            this.panel.querySelector('[data-facets-sort-group] input')
          : null;
      const scroller = this.querySelector('[data-facets-scroll]');
      if (scroller) scroller.scrollTop = 0;
      window.setTimeout(() => (sortTarget || this.panel).focus({ preventScroll: true }), 60);
    }

    closeSheet(returnFocus = true) {
      if (!this.panel || !this.panel.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.classList.remove('is-sheet-open');
      document.body.classList.remove('overflow-hidden-tablet');
      this.querySelectorAll('[data-sheet-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (returnFocus) this.sheetTrigger?.focus();
    }

    onBreakpoint() {
      if (desktop.matches) this.closeSheet(false);
    }

    onPanelKeydown(event) {
      if (!this.panel.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSheet();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (element) => element.getClientRects().length > 0 && !(element.type === 'radio' && !element.checked)
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Shopify standard events, as in Dawn's facets.js.
    startUpdateEvent(searchParams) {
      const { CollectionUpdateEvent } = window.StandardEvents || {};
      if (!CollectionUpdateEvent) return null;
      const deferred = CollectionUpdateEvent.createPromise();
      const params = new URLSearchParams(searchParams);
      this.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: this.dataset.collectionId || null,
            handle: this.dataset.collectionHandle || '',
            productsCount: Number(this.dataset.resultsCount) || 0,
          },
          productFilters: CollectionUpdateEvent.parseProductFilters(params),
          sortKey: CollectionUpdateEvent.getSortKey(params),
          promise: deferred.promise,
        })
      );
      return {
        resolve: (count) => deferred.resolve({ productsCount: count }),
        reject: deferred.reject,
      };
    }
  }

  customElements.define('collection-facets', CollectionFacets);

  if (!customElements.get('price-range')) {
    customElements.define(
      'price-range',
      class PriceRange extends HTMLElement {
        connectedCallback() {
          this.max = Number(this.dataset.max) || 0;
          this.minRange = this.querySelector('[data-range-min]');
          this.maxRange = this.querySelector('[data-range-max]');
          this.minInput = this.querySelector('[data-price-min]');
          this.maxInput = this.querySelector('[data-price-max]');

          this.minRange?.addEventListener('input', () => this.onRange('min'));
          this.maxRange?.addEventListener('input', () => this.onRange('max'));
          [this.minInput, this.maxInput].forEach((input) => {
            input?.addEventListener('change', () => this.onInput());
            input?.addEventListener('keydown', (event) => {
              if (event.metaKey || event.ctrlKey || event.key.length > 1) return;
              if (!/[0-9.,]/.test(event.key)) event.preventDefault();
            });
          });
        }

        onRange(which) {
          let low = Number(this.minRange.value);
          let high = Number(this.maxRange.value);
          if (low > high) {
            if (which === 'min') {
              low = high;
              this.minRange.value = String(low);
            } else {
              high = low;
              this.maxRange.value = String(high);
            }
          }
          this.minInput.value = low > 0 ? String(low) : '';
          this.maxInput.value = high < this.max ? String(high) : '';
          this.paint(low, high);
        }

        onInput() {
          const parse = (input, fallback) => {
            const value = parseFloat(String(input.value).replace(',', '.'));
            return Number.isFinite(value) ? Math.min(Math.max(value, 0), this.max) : fallback;
          };
          let low = parse(this.minInput, 0);
          let high = parse(this.maxInput, this.max);
          if (low > high) [low, high] = [high, low];
          if (this.minInput.value !== '') this.minInput.value = String(low);
          if (this.maxInput.value !== '') this.maxInput.value = String(high);
          if (this.minRange) this.minRange.value = String(Math.floor(low));
          if (this.maxRange) this.maxRange.value = String(Math.ceil(high));
          this.paint(low, high);
        }

        paint(low, high) {
          if (!this.max) return;
          this.style.setProperty('--range-from', `${(low / this.max) * 100}%`);
          this.style.setProperty('--range-to', `${(high / this.max) * 100}%`);
        }
      }
    );
  }
})();
